import ajax from './ajax'
import { compose, fromPairs, isEmpty, map, pathOr, prop, propOr, sortBy, split, tail, test } from 'ramda'
import { addDays, differenceInCalendarDays, format, isValid, parse as parseDate } from 'date-fns'
import PCLNAnalytics from '@pcln/analytics'

const config = require('isomorphic-config')
const LOCATION_SEARCH_ENDPOINT = config.client.typeahead.location.hotels
const LOCATION_RETAIL_ENDPOINT = config.client.search.retail.hotels
const LOCATION_SEARCH_HOTELS_SUFFIX = config.client.typeahead.location.suffix.hotels

const VALID_QUERY_PARAMS = ['poi-id', 'airport-code', 'city-id', 'city-name', 'check-in', 'check-out', 'rooms']

const parseQs = compose(fromPairs, map(split('=')), split('&'), tail)

const isQSValidForPrePop = queryString => {
  return VALID_QUERY_PARAMS.some(param => queryString.includes(param))
}

const getQsObject = (queryString) => {
  if (!queryString || !queryString.length || !isQSValidForPrePop(queryString)) return {}
  return parseQs(queryString)
}

const cityNameTransform = cityInfo => {
  if (cityInfo) {
    const cityName = cityInfo.cityName ? `${cityInfo.cityName}, ` : ''

    const state =
      cityInfo.countryCode === 'US'
        ? cityInfo.stateCode
        : cityInfo.countryCode === 'CA'
          ? `${cityInfo.stateCode}, `
          : ''

    const country =
      cityInfo.countryCode !== 'US' && cityInfo.countryName
        ? cityInfo.countryName
        : ''

    const cityDisplayName = `${cityName}${state}${country}`

    return cityDisplayName
  }

  return ''
}

const mapLocationEntries = entries =>
  entries.map(entry => {
    const key = entry[0]
    const value = Array.isArray(entry[1])
      ? entry[1][0].trim().toLowerCase()
      : entry[1].trim().toLowerCase()
    let priority

    switch (key) {
      case 'poi-id':
        priority = 1
        break
      case 'airport-code':
        priority = 2
        break
      case 'city-id':
        priority = 3
        break
      case 'city-name':
        priority = 4
        break
    }
    if (priority) return { key, value, priority }
  })

const sortLocationParams = sortBy(prop('priority'))

const parseDateByKey = (qsparams, key) => {
  const d = qsparams[key]
  if (!d) return
  const isDataString = typeof d === 'string'
  if (isDataString && d.toLowerCase() === 'today') {
    return new Date()
  }
  if (isDataString && d.toLowerCase() === 'tomorrow') {
    return addDays(new Date(), 1)
  }

  if (d.match(/^\d{8}$/)) {
    return parseDate(d, 'yyyyMMdd', new Date())
  }

  return new Date(d.replace(/-/g, '/'))
}

const adjustLengthOfStay = (checkInDate, checkOutDate) => {
  const isUnder1Day = differenceInCalendarDays(checkOutDate, checkInDate) < 1
  let adjustedCheckOutDate = isUnder1Day
    ? addDays(checkInDate, 1)
    : checkOutDate

  const isOver28Days = differenceInCalendarDays(checkOutDate, checkInDate) > 28
  if (isOver28Days) adjustedCheckOutDate = addDays(checkInDate, 28)

  return { checkInDate, checkOutDate: adjustedCheckOutDate }
}

const typeaheadApiRequest = value => {
  const url = `${LOCATION_SEARCH_ENDPOINT}${value}${LOCATION_SEARCH_HOTELS_SUFFIX}`

  return ajax({ url })
    .then(pathOr([], ['data', 'searchItems']))
    .catch(err => {
      PCLNAnalytics.logError({
        message: err.message,
        severity: 3,
        stack: 'hotels typeaheadApiRequest'
      })
      throw err
    })
}

const typeaheadRetailSearchApiRequest = value => {
  const url = `${LOCATION_RETAIL_ENDPOINT}${value}`
  return ajax({ url })
    .then(propOr({}, 'data'))
    .catch(err => {
      PCLNAnalytics.logError({
        message: err.message,
        severity: 3,
        stack: 'hotels typeaheadRetailSearchApiRequest'
      })
    })
}

const getHotelSearchLocation = async (qsObject, shouldPrepopCity = false) => {
  const qsEntries = Object.entries(qsObject)
  const mappedEntries = mapLocationEntries(qsEntries)
  const sortedEntries = sortLocationParams(mappedEntries)
  const location = sortedEntries.shift()

  if (!location || isEmpty(location)) {
    return {}
  }
  const { value, priority } = location

  const byPoiId = priority === 1
  const byCityId = priority === 3

  const findLocationByPoiIdOrCityId = res => {
    const displayText = (byPoiId || !shouldPrepopCity)
      ? pathOr('', ['exactMatch', 'displayText'], res)
      : cityNameTransform(pathOr('', ['exactMatch', 'matchedCity'], res))
    const searchedLocation = byPoiId
      ? pathOr('', ['searchedLocation'], res)
      : Object.assign(
        pathOr({}, ['exactMatch', 'matchedCity'], res),
        {
          id: pathOr('', ['exactMatch', 'matchedCity', 'cityID'], res),
          type: 'CITY'
        })
    return {
      displayText,
      itemName: displayText,
      ...searchedLocation
    }
  }
  const byAirportCode = priority === 2
  const byCityName = priority === 4

  const findLocationByAirport = res => res.find(el => el.type === 'AIRPORT')
  const findLocationByCityName = res => res.find(el => el.type === 'CITY')
  const PROMISE_ERROR = `failed to resolve ${value} to populate typeahead`

  if (byAirportCode || byCityName) {
    try {
      const results = await typeaheadApiRequest(value)
      if (results && !isEmpty(results)) {
        const found = byAirportCode
          ? findLocationByAirport(results)
          : findLocationByCityName(results)

        return {
          displayText: found.itemName || '',
          ...found
        }
      }
    } catch (err) {
      PCLNAnalytics.logError({
        message: err.message,
        severity: 3,
        stack: PROMISE_ERROR
      })
      throw err
    }
  }

  if (byPoiId || byCityId) {
    try {
      const results = await typeaheadRetailSearchApiRequest(value)
      if (results && !isEmpty(results)) {
        const found = findLocationByPoiIdOrCityId(results)
        return found
      }
    } catch (err) {
      PCLNAnalytics.logError({
        message: err.message,
        severity: 3,
        stack: PROMISE_ERROR
      })
    }
  }
}

const getCheckInOutDates = (qsparams) => {
  const parsedCheckInDate = parseDateByKey(qsparams, 'check-in')
  const parsedCheckOutDate = parseDateByKey(qsparams, 'check-out')

  if (!isValid(parsedCheckInDate) || !isValid(parsedCheckOutDate)) return
  const { checkInDate, checkOutDate } = adjustLengthOfStay(
    parsedCheckInDate,
    parsedCheckOutDate
  )
  const checkIn = format(checkInDate, 'yyyyMMdd')
  const checkOut = format(checkOutDate, 'yyyyMMdd')
  return { checkIn, checkOut }
}

const getRooms = (qsparams) => {
  let parsedRoomsNumber = qsparams['rooms']
  if (!parsedRoomsNumber || !test(/^\d+$/, parsedRoomsNumber) || (parseInt(parsedRoomsNumber) < 1)) {
    parsedRoomsNumber = 1
  } else if (parseInt(parsedRoomsNumber) > 10) {
    parsedRoomsNumber = 10
  }
  return parsedRoomsNumber
}

export {
  cityNameTransform,
  getCheckInOutDates,
  getHotelSearchLocation,
  getQsObject,
  getRooms,
  isQSValidForPrePop
}
